'use client';

/**
 * StepCards — V3 dark, numbered step-card grid.
 *
 * Shared across feature pages (/auto-pilot, /payments, /e-signature) that
 * present a 3–4 step setup arc. Each card has: numbered kicker + horizontal
 * rule + corner icon, title, description, and an optional bullet list and
 * image slot. An optional arrow connects cards on desktop.
 *
 * Color accent is per-section (cyan for /auto-pilot + /payments, violet for
 * /e-signature). The card chrome and typography are fixed.
 */

import React from 'react';
import Image from 'next/image';
import { HiArrowRight } from 'react-icons/hi2';

export type StepCardItem = {
  key: string;
  num: string;
  title: string;
  description: string;
  details?: string[];
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  image?: string;
  /** Optional content rendered at the bottom of the card, after details.
   *  Used for store badges, secondary CTAs, etc. */
  footer?: React.ReactNode;
};

type Props = {
  steps: StepCardItem[];
  accent?: string;
  minCardWidth?: number;
  showArrow?: boolean;
};

const StepCards = ({ steps, accent = '#22D3EE', minCardWidth = 280, showArrow = false }: Props) => {
  const accentTint = (alpha: number) => {
    // accent is a hex like #22D3EE — convert to rgba with the given alpha
    const hex = accent.replace('#', '');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };

  return (
    <div className="grid gap-5" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minCardWidth}px, 1fr))` }}>
      {steps.map(({ key, num, title, description, details, Icon, image, footer }, i) => {
        const isLast = i === steps.length - 1;
        return (
          <div
            key={key}
            className="relative rounded-2xl p-6 flex flex-col"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="mono" style={{ fontSize: 11, letterSpacing: 2, color: accent }}>
                {num}
              </span>
              <span
                aria-hidden="true"
                style={{
                  flex: 1,
                  height: 1,
                  background: accentTint(0.2),
                }}
              />
              <div
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg"
                style={{
                  background: accentTint(0.12),
                  border: `1px solid ${accentTint(0.3)}`,
                }}
              >
                <Icon className="w-5 h-5" style={{ color: accent }} />
              </div>
            </div>

            {image && (
              <div
                className="relative w-full mb-5 rounded-xl overflow-hidden"
                style={{
                  aspectRatio: '4 / 3',
                  background: 'rgba(10,14,39,0.5)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <Image
                  src={image}
                  alt={title}
                  fill
                  sizes="(max-width: 768px) 100vw, 360px"
                  className="object-contain p-3"
                />
              </div>
            )}

            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                letterSpacing: '-0.01em',
                color: '#F5F7FA',
                margin: '0 0 8px',
              }}
            >
              {title}
            </h3>
            <p
              style={{
                color: '#B4C1D8',
                fontSize: 14,
                lineHeight: 1.55,
                margin: details && details.length > 0 ? '0 0 14px' : 0,
              }}
            >
              {description}
            </p>

            {details && details.length > 0 && (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {details.map((d, di) => (
                  <li key={di} className="flex items-start gap-2" style={{ marginBottom: 6 }}>
                    <span
                      aria-hidden="true"
                      className="flex-shrink-0"
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: 999,
                        background: accent,
                        marginTop: 8,
                      }}
                    />
                    <span style={{ color: '#B4C1D8', fontSize: 13.5, lineHeight: 1.5 }}>{d}</span>
                  </li>
                ))}
              </ul>
            )}

            {footer && <div style={{ marginTop: 16 }}>{footer}</div>}

            {showArrow && !isLast && (
              <HiArrowRight
                aria-hidden="true"
                className="hidden lg:block absolute"
                style={{
                  right: -16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 18,
                  height: 18,
                  color: '#3A3F5C',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StepCards;
